import type { Communication, Organization } from '@/api/olympe'

/**
 * Conversion des « communications » de l'API en affiches du module « À la une ».
 *
 * Module pur — pas de Vue, pas d'état : `useCompetition` appelle `toHighlights`
 * en construisant la compétition (les communications actives voyagent dans la
 * réponse de `/organizations/current`), `useCommunications` se contente ensuite
 * de croiser le résultat avec le challenge sélectionné.
 */

export interface Highlight {
  id: number
  /** épreuve ciblée ; `null` = l'affiche vise toute l'organisation */
  challengeId: number | null
  /** `name` de l'API, ou null quand c'est le nom par défaut « communication » */
  title: string | null
  text: string
  /** mobileImage de préférence, sinon image, sinon null */
  image: string | null
  url: string | null
  startsAt: Date
  endsAt: Date
}

/** Langues préférées, dans l'ordre ; à défaut on prend la première non vide. */
const LOCALES: readonly string[] = ['fr', 'en']

/** Libellé par défaut posé par le back-office : ce n'est pas un vrai titre. */
const DEFAULT_NAME = 'communication'

function nonEmpty(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

/** fr → en → première locale non vide ; null si la communication n'a pas de texte. */
function pickText(text: Record<string, string> | null | undefined): string | null {
  const values = text ?? {}

  for (const locale of LOCALES) {
    const preferred = nonEmpty(values[locale])
    if (preferred) return preferred
  }

  for (const value of Object.values(values)) {
    const fallback = nonEmpty(value)
    if (fallback) return fallback
  }

  return null
}

function pickTitle(name: string | null | undefined): string | null {
  const trimmed = nonEmpty(name)
  if (!trimmed || trimmed.toLowerCase() === DEFAULT_NAME) return null
  return trimmed
}

function toHighlight(communication: Communication): Highlight | null {
  // une affiche sans texte n'a rien à raconter : on la jette
  const text = pickText(communication.text)
  if (!text) return null

  return {
    id: communication.id,
    challengeId: communication.challengeID,
    title: pickTitle(communication.name),
    text,
    image: nonEmpty(communication.mobileImage) ?? nonEmpty(communication.image),
    url: nonEmpty(communication.targetURL),
    startsAt: new Date(communication.startDate * 1000),
    endsAt: new Date(communication.endDate * 1000),
  }
}

/**
 * L'affiche est-elle dans sa fenêtre de diffusion ?
 *
 * Ceinture et bretelles : l'API ne rend que les communications en cours, mais
 * la liste est figée au chargement du catalogue et une app peut tourner des
 * heures. Le test se fait donc **au rendu** (`useCommunications`), jamais à la
 * conversion — sans quoi il ne vérifierait rien que l'API n'ait déjà vérifié.
 *
 * `nowSeconds` en secondes, comme les dates de l'API.
 */
export function isActive(highlight: Highlight, nowSeconds: number): boolean {
  const now = nowSeconds * 1000
  const start = highlight.startsAt.getTime()
  const end = highlight.endsAt.getTime()
  // une borne illisible (l'API a envoyé autre chose qu'un timestamp) ne doit
  // pas faire disparaître l'affiche : on ne retient que les bornes valides
  return (
    (Number.isNaN(start) || start <= now) && (Number.isNaN(end) || now <= end)
  )
}

/**
 * Aplatit toutes les listes de l'objet (`slider`, et tout type que l'API
 * ajouterait), de la campagne la plus récemment démarrée à la plus ancienne.
 *
 * Les valeurs de l'objet sont censées être des tableaux de communications ;
 * tout ce qui n'en est pas une est ignoré plutôt que de faire tomber la
 * conversion — donc le chargement de toute la compétition.
 */
export function toHighlights(
  communications: Organization['communications'] | null | undefined,
): Highlight[] {
  return Object.values(communications ?? {})
    .flatMap((list) => (Array.isArray(list) ? (list as unknown[]) : []))
    .flatMap((item) => {
      if (typeof item !== 'object' || item === null) return []
      const highlight = toHighlight(item as Communication)
      return highlight ? [highlight] : []
    })
    .sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime())
}
